import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, HashRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './app/AppShell';
import { SessionProvider, useSession } from './lib/session';
import { AiAssistant } from './screens/AiAssistant';
import { Audit } from './screens/Audit';
import { Companies } from './screens/Companies';
import { Compliance } from './screens/Compliance';
import { Counterparties } from './screens/Counterparties';
import { CounterpartyDetail } from './screens/CounterpartyDetail';
import { Dashboard } from './screens/Dashboard';
import { DealDetail } from './screens/DealDetail';
import { Deals } from './screens/Deals';
import { DocumentComposer } from './screens/DocumentComposer';
import { DocumentDetail } from './screens/DocumentDetail';
import { Documents } from './screens/Documents';
import { Kyc } from './screens/Kyc';
import { Logistics } from './screens/Logistics';
import { Login } from './screens/Login';
import { ModulePlaceholder } from './screens/ModulePlaceholder';
import { Objectives } from './screens/Objectives';
import { Profitability } from './screens/Profitability';
import { Accounting } from './screens/Accounting';
import { Projects } from './screens/Projects';
import { Recurring } from './screens/Recurring';
import { Search } from './screens/Search';
import { SignPage } from './screens/SignPage';
import { DocView } from './screens/DocView';
import { CounterpartyView } from './screens/CounterpartyView';
import { Subscription } from './screens/Subscription';
import { Templates } from './screens/Templates';
import { Treasury } from './screens/Treasury';
import { Users } from './screens/Users';
import './styles/tokens.css';

function Splash() {
  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--paper)' }}>
      <div className="col center gap-12">
        <span className="brandmark" style={{ width: 40, height: 40, fontSize: 22 }}>N</span>
        <span className="faint" style={{ fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '0.1em' }}>
          CARGANDO…
        </span>
      </div>
    </div>
  );
}

function AppShellRoutes() {
  const { ready, me } = useSession();
  if (!ready) return <Splash />;
  if (!me) return <Login />;

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<Dashboard />} />
        <Route path="objetivos" element={<Objectives />} />
        <Route path="ia" element={<AiAssistant />} />
        <Route path="relaciones" element={<Counterparties />} />
        <Route path="relaciones/:id" element={<CounterpartyDetail />} />
        <Route path="cumplimiento/kyc" element={<Kyc />} />
        <Route path="operaciones/deals" element={<Deals />} />
        <Route path="operaciones/:id" element={<DealDetail />} />
        <Route path="documentos" element={<Documents />} />
        <Route path="documentos/:id" element={<DocumentDetail />} />
        <Route path="ventas/cotizaciones/nueva" element={<DocumentComposer />} />
        <Route path="ventas/contratos/nuevo" element={<DocumentComposer />} />
        <Route path="ventas/facturas/nueva" element={<DocumentComposer />} />
        <Route path="compras/gastos/nuevo" element={<DocumentComposer />} />
        <Route path="finanzas/pagos/nuevo" element={<DocumentComposer />} />
        <Route path="operaciones/loi/nueva" element={<DocumentComposer />} />
        <Route path="ventas/cotizaciones" element={<Documents />} />
        <Route path="ventas/contratos" element={<Documents />} />
        <Route path="ventas/facturas" element={<Documents />} />
        <Route path="ventas/recurrentes" element={<Recurring />} />
        <Route path="finanzas/pagos" element={<Documents />} />
        <Route path="finanzas/tesoreria" element={<Treasury />} />
        <Route path="finanzas/rentabilidad" element={<Profitability />} />
        <Route path="finanzas/contabilidad" element={<Accounting />} />
        <Route path="compras/gastos" element={<Documents />} />
        <Route path="compras" element={<Documents />} />
        <Route path="operaciones/proyectos" element={<Projects />} />
        <Route path="operaciones/logistica" element={<Logistics />} />
        <Route path="operaciones/loi" element={<Documents />} />
        <Route path="cumplimiento/licencias" element={<Compliance />} />
        <Route path="cumplimiento/vencimientos" element={<Compliance />} />
        <Route path="empresas" element={<Companies />} />
        <Route path="administracion/plantillas" element={<Templates />} />
        <Route path="administracion/usuarios" element={<Users />} />
        <Route path="administracion/auditoria" element={<Audit />} />
        <Route path="administracion/suscripcion" element={<Subscription />} />
        <Route path="buscar" element={<Search />} />
        <Route path="*" element={<ModulePlaceholder />} />
      </Route>
      <Route path="/login" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function Root() {
  const Router = import.meta.env.VITE_PREVIEW ? HashRouter : BrowserRouter;
  return (
    <Router basename={import.meta.env.VITE_PREVIEW ? undefined : (import.meta.env.BASE_URL || '/')}>
      <Routes>
        <Route path="/firmar/:token" element={<SignPage />} />
        <Route path="/ver/:token" element={<DocView />} />
        <Route path="/ficha/:token" element={<CounterpartyView />} />
        <Route path="/*" element={<AppShellRoutes />} />
      </Routes>
    </Router>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <SessionProvider>
      <Root />
    </SessionProvider>
  </StrictMode>,
);
